/**
 * Open the Cashfree hosted-checkout inside Chrome Custom Tabs (Android) or
 * SafariViewController (iOS) — i.e. a real browser instance, NOT the Capacitor
 * WebView. Cashfree (and most Indian payment gateways) refuse to render
 * inside a WebView for PCI compliance reasons; this opens them in a "real
 * browser" overlay that supports 3DS, OTP, autofill, password managers and
 * proper redirects.
 *
 * IMPORTANT — Cashfree PG v3 Orders API does NOT expose a direct URL you can
 * open with a `paymentSessionId`. Their v3 checkout can ONLY be launched via
 * the Cashfree JS SDK `cashfree.checkout({ paymentSessionId })` call. So we
 * ship a tiny bridge page at `/cashfree-checkout.html` that loads the SDK,
 * reads the session ID from the URL, and hands off to Cashfree. Native app
 * opens this bridge URL in Chrome Custom Tabs; the SDK then navigates to
 * the real payments.cashfree.com page inside that same browser tab.
 *
 * After payment, the user taps the in-app browser's close button → we get
 * a `browserFinished` event → caller polls `/verify-payment` to learn the
 * outcome (success, failed, abandoned).
 */
import { Capacitor } from '@capacitor/core';

// Public HTTPS origin where the bridge page is hosted. We default to the
// backend URL because the backend ALWAYS exists for the app to function
// (it's where /api lives). Hosting the bridge on the backend means the
// payment flow works regardless of the PWA deployment status — useful for
// closed-testing builds where penguinbeancoffee.in might not yet have the
// latest build. Override via REACT_APP_PUBLIC_WEB_ORIGIN if you need to
// host the bridge elsewhere.
const FRONTEND_PUBLIC_ORIGIN =
  process.env.REACT_APP_PUBLIC_WEB_ORIGIN ||
  process.env.REACT_APP_BACKEND_URL ||
  'https://orders.penguinbeancoffee.com';

export function isNativeApp() {
  try { return Capacitor?.isNativePlatform?.() === true; } catch { return false; }
}

/**
 * Returns the originating platform string for an order: 'android' | 'ios' | 'web'.
 * Sent on the order POST so admin analytics can split orders by app vs PWA.
 * PWA installs ("Add to Home Screen") report 'web' — the 3-way split is
 * intentional (see CLAUDE.md "Orders by Platform").
 */
export function getClientPlatform() {
  try {
    const p = Capacitor?.getPlatform?.();
    if (p === 'android' || p === 'ios') return p;
    return 'web';
  } catch {
    return 'web';
  }
}

/**
 * Build the URL of our own hosted bridge page. The bridge loads Cashfree's
 * JS SDK and calls `cashfree.checkout({ paymentSessionId })` to initiate
 * the hosted payment flow — which is the only supported path in PG v3.
 *
 * @param {string} paymentSessionId - returned by backend /api/orders
 * @param {'sandbox'|'production'} mode
 * @param {string} [redirectOrigin] - origin to navigate back to if user lands
 *   in the browser after payment (rare edge — most flows redirect via the
 *   return_url Cashfree stores server-side).
 */
export function cashfreeHostedUrl(paymentSessionId, mode = 'production', redirectOrigin) {
  const params = new URLSearchParams({
    session: paymentSessionId,
    mode,
    redirect: redirectOrigin || FRONTEND_PUBLIC_ORIGIN,
  });
  return `${FRONTEND_PUBLIC_ORIGIN}/cashfree-checkout.html?${params.toString()}`;
}

/**
 * Open the URL in a system browser overlay (Chrome Custom Tabs on Android,
 * SafariViewController on iOS) and resolve when the user closes it. The
 * caller is then expected to verify payment status via the backend.
 */
export async function openInSystemBrowser(url) {
  const { Browser } = await import('@capacitor/browser');
  return new Promise(async (resolve) => {
    let listenerHandle;
    try {
      listenerHandle = await Browser.addListener('browserFinished', () => {
        try { listenerHandle?.remove?.(); } catch { /* ignore */ }
        resolve({ closed: true });
      });
    } catch { /* listener API differs across versions; ignore */ }

    try {
      await Browser.open({
        url,
        presentationStyle: 'fullscreen',
        windowName: '_self',
      });
      // Some Capacitor versions emit no event on iOS dismiss — fall back to
      // visibility change, but the listener is the primary path.
    } catch (err) {
      try { listenerHandle?.remove?.(); } catch { /* ignore */ }
      resolve({ closed: false, error: err?.message });
    }
  });
}
